'use client';

import { useContext } from 'react';

import { BookmarkContext } from './bookmark-provider';

export type BookmarkFolderDoc = { id: string } & Record<string, any>;

export interface BookmarkContextValue {
  folders: BookmarkFolderDoc[];
  bookmarkedPostIds: Set<string>;
  postIdToBookmarkFolderIds: Record<string, string[]>;
  mergedBookmarkPostListDocs: Record<string, any>[];
  allActiveBookmarkPostListDocs: Record<string, any>[];
  isPostBookmarked: (postId: unknown) => boolean;
  isPostBookmarkedInFolder: (postId: unknown, folderId: unknown) => boolean;
  createFolder: (payload: Record<string, any>) => Promise<any>;
  addPostToFolder: (folderId: string, postId: string, source?: string) => Promise<any>;
  removePostFromFolder: (folderId: string, postId: string) => Promise<any>;
  updateFolder: (folderId: string, payload: Record<string, any>) => Promise<any>;
  deleteFolder: (folderId: string, updatedBy?: string) => Promise<any>;
  removeAllPostsInFolder: (folderId: string) => Promise<any>;
  subscribeBookmarksByWorkspace: (workspaceId: string, onChange?: (items: any[]) => void) => any;
  subscribePostList: (folderId: string, onChange: (items: any[]) => void) => any;
  subscribeAllPostLists: (...args: any[]) => any;
}

/**
 * BookmarkProvider 하위에서 북마크 폴더 상태·액션을 사용합니다.
 *
 * - `folders` — Firestore 폴더 문서(id, title, postCount, updatedAt, createdAt …)
 * - `bookmarkedPostIds` / `isPostBookmarked(postId)` — 현재 워크스페이스에 저장된 포스트 id
 * - `postIdToBookmarkFolderIds` / `isPostBookmarkedInFolder(postId, folderId)` — 폴더별 소속
 * - `mergedBookmarkPostListDocs` / `allActiveBookmarkPostListDocs` — 전역 collectionGroup 구독 결과
 *
 * ⚠️ **반환 타입은 provider 의 `value` 객체와 한 짝이다.** `bookmark-provider` 가
 * `createContext(null)` 이라, 이 타입이 없으면 TS 가 반환을 `never` 로 좁혀 `.ts` 소비처가
 * 필드를 하나도 못 읽는다. **좁힌 곳은 없다** — 문서 모양은 `Record<string, any>` 로 열어 둔다.
 *
 * ⚠️ 뮤테이터 여섯(`createFolder`·`addPostToFolder`·`removePostFromFolder`·`updateFolder`·
 * `deleteFolder`·`removeAllPostsInFolder`)은 워크스페이스·로그인 가드를 안에 갖고 있다.
 * `actions/firestore/sdk/profile` 을 직접 부르면 그 가드를 건너뛴다.
 */
export function useBookmark(): BookmarkContextValue {
  const ctx = useContext(BookmarkContext) as BookmarkContextValue | null;
  if (ctx == null) {
    throw new Error('useBookmark는 BookmarkProvider 안에서만 사용할 수 있습니다.');
  }
  return ctx;
}
